import { useQuery } from '@tanstack/react-query'
import { getContentByEmbedId, getContentTypeByEmbed } from '../services/contentService'
import type { ContentFilters } from '../services/contentService'

// Claves para React Query
export const contentKeys = {
  all: ['content'] as const,
  byEmbedId: (embedId: string, encriptedString?: string, filters?: ContentFilters) => {
    // Incluir los filtros directamente en la queryKey para que React Query detecte cambios
    return [
      ...contentKeys.all, 
      'embedId', 
      embedId, 
      encriptedString,
      filters // Incluir el objeto de filtros directamente
    ].filter(Boolean)
  },
  contentTypeByEmbedId: (embedId: string) => [...contentKeys.all, 'contentType', embedId] as const,
}

// Hook para obtener contenido por embedId
export const useContentByEmbed = (
  embedId: string, 
  encriptedString?: string, 
  filters?: ContentFilters
) => {
  const enabled = !!embedId
  const queryKey = contentKeys.byEmbedId(embedId, encriptedString, filters)

  console.log('=== useContentByEmbed Debug ===')
  console.log('embedId', embedId)
  console.log('enabled', enabled)
  console.log('filters', filters)
  console.log('queryKey', queryKey)

  return useQuery({
    queryKey,
    queryFn: () => {
      console.log('queryFn executing for embedId:', embedId, 'with filters:', filters);
      return getContentByEmbedId(embedId, encriptedString, filters)
    },
    enabled,
    // Los datos del MLS cambian en escala de horas; con 30s cualquier vuelta atras
    // re-disparaba la descarga completa del listado.
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos en memoria
    retry: (failureCount, error: any) => {
      // No reintentar si es error de autenticación
      if (error?.response?.status === 401) {
        return false
      }
      // El listado es grande y lento: 3 reintentos multiplicaban la carga sobre un
      // backend que ya estaba sufriendo. Con uno alcanza para un fallo transitorio.
      return failureCount < 1
    },
    refetchOnWindowFocus: false,
  })
}

// Hook para obtener el tipo de contenido por embedId
export const useContentTypeByEmbed = (embedId: string) => {
  return useQuery({
    queryKey: contentKeys.contentTypeByEmbedId(embedId),
    queryFn: () => getContentTypeByEmbed(embedId),
    enabled: !!embedId,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    retry: (failureCount, error: any) => {
      // No reintentar si es error de autenticación
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 3
    },
    refetchOnWindowFocus: false,
  })
}

// Hook específico para FeaturedPropertiesCarousel
export const useFeaturedProperties = () => {
  return useContentByEmbed('FeaturedPropertiesCarousel')
}

export default useContentByEmbed
